import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import './MyBookings.css';

const MyBookings = () => {
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);

  const t = (key) => {
    const tr = {
      myBookings: { en: 'My Bookings', ar: 'مواعيدي' },
      bookNew: { en: 'Book New Meeting', ar: 'حجز موعد جديد' },
      upcoming: { en: 'Upcoming', ar: 'القادمة' },
      past: { en: 'Past', ar: 'السابقة' },
      all: { en: 'All', ar: 'الكل' },
      noBookings: { en: 'No bookings found', ar: 'لا توجد مواعيد' },
      noUpcoming: { en: 'No upcoming bookings', ar: 'لا توجد مواعيد قادمة' },
      date: { en: 'Date', ar: 'التاريخ' },
      time: { en: 'Time', ar: 'الوقت' },
      agent: { en: 'Agent', ar: 'الموظف' },
      type: { en: 'Type', ar: 'النوع' },
      status: { en: 'Status', ar: 'الحالة' },
      join: { en: 'Join Meeting', ar: 'انضم للاجتماع' },
      pending: { en: 'Pending', ar: 'قيد الانتظار' },
      confirmed: { en: 'Confirmed', ar: 'مؤكد' },
      cancelled: { en: 'Cancelled', ar: 'ملغي' },
      completed: { en: 'Completed', ar: 'مكتمل' },
      no_show: { en: 'No Show', ar: 'لم يحضر' },
      loading: { en: 'Loading...', ar: 'جاري التحميل...' },
      connection: { en: 'Connection', ar: 'طريقة الاتصال' },
      reason: { en: 'Reason', ar: 'السبب' },
      bookFirstMeeting: { en: 'Book your first meeting!', ar: 'احجز أول موعد لك!' },
      cancelBooking: { en: 'Cancel Booking', ar: 'إلغاء الموعد' },
      rescheduleBooking: { en: 'Reschedule', ar: 'تأجيل الموعد' },
      confirmCancel: { en: 'Are you sure you want to cancel this booking?', ar: 'هل أنت متأكد من إلغاء هذا الموعد؟' },
      cancelSuccess: { en: 'Booking cancelled successfully', ar: 'تم إلغاء الموعد بنجاح' },
      cancelFailed: { en: 'Failed to cancel booking', ar: 'فشل إلغاء الموعد' },
      rescheduleTitle: { en: 'Reschedule Meeting', ar: 'تأجيل الموعد' },
      selectNewDate: { en: 'Select new date', ar: 'اختر التاريخ الجديد' },
      selectNewTime: { en: 'Select new time', ar: 'اختر الوقت الجديد' },
      noSlots: { en: 'No available slots', ar: 'لا توجد مواعيد متاحة' },
      confirmReschedule: { en: 'Confirm Reschedule', ar: 'تأكيد التأجيل' },
      rescheduleSuccess: { en: 'Meeting rescheduled successfully', ar: 'تم تأجيل الموعد بنجاح' },
      rescheduleFailed: { en: 'Failed to reschedule', ar: 'فشل تأجيل الموعد' },
      close: { en: 'Close', ar: 'إغلاق' },
      loadingSlots: { en: 'Loading available times...', ar: 'جاري تحميل المواعيد المتاحة...' },
      currentBooking: { en: 'Current booking', ar: 'الموعد الحالي' },
      newBooking: { en: 'New date & time', ar: 'التاريخ والوقت الجديد' },
    };
    return tr[key]?.[language] || tr[key]?.['en'] || key;
  };

  useEffect(() => { loadBookings(); }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/helpdesk/my-bookings');
      if (res.data.success) setBookings(res.data.data || []);
    } catch (err) { console.error('Failed to load bookings:', err); }
    finally { setLoading(false); }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(t('confirmCancel'))) return;
    try {
      const res = await api.post(`/helpdesk/bookings/${bookingId}/cancel`);
      if (res.data.success) { alert(t('cancelSuccess')); loadBookings(); }
      else { alert(res.data.message || t('cancelFailed')); }
    } catch (err) { alert(t('cancelFailed')); }
  };

  const openReschedule = async (booking) => {
    setRescheduleBooking(booking);
    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setLoadingSlots(true);
    try {
      const res = await api.get(`/bookings/slots/${booking.agent_id}`, { params: { meeting_type: booking.meeting_type } });
      if (res.data.success) setAvailableSlots(res.data.data || []);
    } catch (err) { console.error('Failed to load slots:', err); }
    finally { setLoadingSlots(false); }
  };

  const handleReschedule = async () => {
    if (!selectedSlot || !rescheduleBooking) return;
    setRescheduling(true);
    try {
      const res = await api.post(`/helpdesk/bookings/${rescheduleBooking.id}/reschedule`, {
        booking_date: selectedSlot.date, start_time: selectedSlot.start_time, end_time: selectedSlot.end_time
      });
      if (res.data.success) { alert(t('rescheduleSuccess')); setRescheduleBooking(null); loadBookings(); }
      else { alert(res.data.message || t('rescheduleFailed')); }
    } catch (err) { alert(t('rescheduleFailed')); }
    finally { setRescheduling(false); }
  };

  const slotsByDate = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});
  const availableDates = Object.keys(slotsByDate).sort();

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const getStatusClass = (s) => {
    const m = { confirmed: 'mb-status-confirmed', pending: 'mb-status-pending', cancelled: 'mb-status-cancelled', completed: 'mb-status-completed' };
    return m[s] || 'mb-status-pending';
  };

  const getConnectionLabel = (method) => {
    const labels = { 'google_meet': 'Google Meet', 'zoom': 'Zoom', 'teams': 'Microsoft Teams',
      'phone_call': language === 'ar' ? 'مكالمة هاتفية' : 'Phone Call',
      'in_person': language === 'ar' ? 'حضوري' : 'In Person' };
    return labels[method] || method || '-';
  };

  const getMeetingTypeLabel = (type) => {
    const labels = { 'technical-support': language === 'ar' ? 'دعم فني' : 'Technical Support',
      'sales-consultation': language === 'ar' ? 'استشارة مبيعات' : 'Sales Consultation',
      'quotation-discussion': language === 'ar' ? 'مناقشة عرض سعر' : 'Quotation Discussion' };
    return labels[type] || type || '-';
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const filteredBookings = bookings.filter(b => {
    const bDate = new Date(b.booking_date).toISOString().split('T')[0];
    if (filter === 'upcoming') return bDate >= todayStr && b.status !== 'cancelled';
    if (filter === 'past') return bDate < todayStr || b.status === 'cancelled' || b.status === 'completed';
    return true;
  });

  if (loading) return (
    <div className={`mb-container ${isRTL ? 'rtl' : ''}`}>
      <div className="mb-loading"><div className="mb-spinner"></div><p>{t('loading')}</p></div>
    </div>
  );

  return (
    <div className={`mb-container ${isRTL ? 'rtl' : ''}`}>
      <div className="mb-header">
        <h2>{t('myBookings')}</h2>
        <button className="mb-btn-new" onClick={() => navigate('/book-meeting')}>+ {t('bookNew')}</button>
      </div>

      <div className="mb-tabs">
        {['upcoming', 'past', 'all'].map(f => (
          <button key={f} className={`mb-tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {t(f)}
            {f === 'upcoming' && <span className="mb-tab-count">{bookings.filter(b => { const bd = new Date(b.booking_date).toISOString().split('T')[0]; return bd >= todayStr && b.status !== 'cancelled'; }).length}</span>}
          </button>
        ))}
      </div>

      {filteredBookings.length === 0 ? (
        <div className="mb-empty">
          <div className="mb-empty-icon">📅</div>
          <p>{filter === 'upcoming' ? t('noUpcoming') : t('noBookings')}</p>
          {filter === 'upcoming' && <button className="mb-btn-book" onClick={() => navigate('/book-meeting')}>{t('bookFirstMeeting')}</button>}
        </div>
      ) : (
        <div className="mb-list">
          {filteredBookings.map((booking) => {
            const bDate = new Date(booking.booking_date).toISOString().split('T')[0];
            const isUpcoming = bDate >= todayStr && booking.status !== 'cancelled';
            return (
              <div key={booking.id} className={`mb-card ${isUpcoming ? 'mb-card-upcoming' : ''}`}>
                <div className="mb-card-header">
                  <div className="mb-card-date"><span className="mb-card-icon">📅</span> {formatDate(booking.booking_date)}</div>
                  <span className={`mb-status ${getStatusClass(booking.status)}`}>{t(booking.status) || booking.status}</span>
                </div>
                <div className="mb-card-body">
                  <div className="mb-card-row"><span className="mb-card-label">⏰ {t('time')}</span><span className="mb-card-value">{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span></div>
                  <div className="mb-card-row"><span className="mb-card-label">👤 {t('agent')}</span><span className="mb-card-value">{booking.agent_name}</span></div>
                  <div className="mb-card-row"><span className="mb-card-label">📋 {t('type')}</span><span className="mb-card-value">{getMeetingTypeLabel(booking.meeting_type)}</span></div>
                  <div className="mb-card-row"><span className="mb-card-label">💻 {t('connection')}</span><span className="mb-card-value">{getConnectionLabel(booking.connection_method)}</span></div>
                  {booking.meeting_reason && <div className="mb-card-row"><span className="mb-card-label">📝 {t('reason')}</span><span className="mb-card-value">{booking.meeting_reason}</span></div>}
                </div>
                {booking.google_meet_link && (
                  <div className="mb-card-footer">
                    <a href={booking.google_meet_link} target="_blank" rel="noopener noreferrer" style={{ color: '#4285f4', fontSize: '13px', wordBreak: 'break-all', display: 'block', marginBottom: '8px' }}>🔗 {booking.google_meet_link}</a>
                    {isUpcoming && <a href={booking.google_meet_link} target="_blank" rel="noopener noreferrer" className="mb-btn-join">🎥 {t('join')}</a>}
                  </div>
                )}
                {isUpcoming && booking.status !== 'cancelled' && (
                  <div style={{ display: 'flex', gap: '10px', padding: '12px 16px', borderTop: '1px solid #eee' }}>
                    <button onClick={() => openReschedule(booking)} style={{ flex: 1, padding: '8px 16px', borderRadius: '8px', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>📅 {t('rescheduleBooking')}</button>
                    <button onClick={() => handleCancelBooking(booking.id)} style={{ flex: 1, padding: '8px 16px', borderRadius: '8px', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>❌ {t('cancelBooking')}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleBooking && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }} onClick={() => setRescheduleBooking(null)}>
          <div style={{ background: '#fff', borderRadius: '16px', maxWidth: '500px', width: '100%', maxHeight: '80vh', overflow: 'auto', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>📅 {t('rescheduleTitle')}</h3>
              <button onClick={() => setRescheduleBooking(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '10px', padding: '12px', marginBottom: '20px', fontSize: '13px', color: '#92400e' }}>
              <strong>{t('currentBooking')}:</strong><br/>
              📅 {formatDate(rescheduleBooking.booking_date)} &nbsp; ⏰ {formatTime(rescheduleBooking.start_time)} - {formatTime(rescheduleBooking.end_time)}<br/>
              👤 {rescheduleBooking.agent_name}
            </div>
            {loadingSlots ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}><div className="mb-spinner"></div><p>{t('loadingSlots')}</p></div>
            ) : availableDates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}><p>{t('noSlots')}</p></div>
            ) : (
              <>
                <p style={{ fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '10px' }}>1️⃣ {t('selectNewDate')}</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                  {availableDates.map(date => (
                    <button key={date} onClick={() => { setSelectedDate(date); setSelectedSlot(null); }} style={{
                      padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      background: selectedDate === date ? '#3b82f6' : '#f3f4f6',
                      color: selectedDate === date ? '#fff' : '#374151',
                      border: selectedDate === date ? '2px solid #2563eb' : '1px solid #d1d5db',
                    }}>{formatShortDate(date)}</button>
                  ))}
                </div>
                {selectedDate && (
                  <>
                    <p style={{ fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '10px' }}>2️⃣ {t('selectNewTime')}</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                      {(slotsByDate[selectedDate] || []).map((slot, idx) => (
                        <button key={idx} onClick={() => setSelectedSlot(slot)} style={{
                          padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          background: selectedSlot === slot ? '#10b981' : '#f3f4f6',
                          color: selectedSlot === slot ? '#fff' : '#374151',
                          border: selectedSlot === slot ? '2px solid #059669' : '1px solid #d1d5db',
                        }}>{formatTime(slot.start_time)} - {formatTime(slot.end_time)}</button>
                      ))}
                    </div>
                  </>
                )}
                {selectedSlot && (
                  <div style={{ background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '10px', padding: '12px', marginBottom: '20px', fontSize: '13px', color: '#065f46' }}>
                    <strong>{t('newBooking')}:</strong><br/>
                    📅 {formatShortDate(selectedSlot.date)} &nbsp; ⏰ {formatTime(selectedSlot.start_time)} - {formatTime(selectedSlot.end_time)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setRescheduleBooking(null)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>{t('close')}</button>
                  <button onClick={handleReschedule} disabled={!selectedSlot || rescheduling} style={{
                    flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                    background: selectedSlot ? '#3b82f6' : '#d1d5db', color: '#fff', fontSize: '14px', fontWeight: 600,
                    cursor: selectedSlot ? 'pointer' : 'not-allowed', opacity: rescheduling ? 0.6 : 1
                  }}>{rescheduling ? '...' : `✅ ${t('confirmReschedule')}`}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookings;
